import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Zap, BarChart3, Code2, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PrismHero } from '../components/home/PrismHero';
import { GlassCard } from '../components/ui/GlassCard';
import { useEmbed, navigateTop } from '../hooks/useEmbed';

export const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEmbed = useEmbed();

  const features = [
    {
      icon: <Zap className="w-6 h-6 text-glass-cyan" />,
      title: t('home.featureUnifiedApi'),
      description: t('home.featureUnifiedApiDesc'),
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-glass-cyan" />,
      title: t('home.featureGovernance'),
      description: t('home.featureGovernanceDesc'),
    },
    {
      icon: <Shield className="w-6 h-6 text-glass-cyan" />,
      title: t('home.featureReliability'),
      description: t('home.featureReliabilityDesc'),
    },
  ];

  const handleGetStarted = () => {
    if (isEmbed) {
      navigateTop('https://api.getinfinityblue.com/register');
    } else {
      navigate('/pricing');
    }
  };

  const handleTopUp = () => {
    if (isEmbed) {
      navigateTop('https://api.getinfinityblue.com/pricing');
    } else {
      navigate('/pricing');
    }
  };

  return (
    <div className="space-y-32 pb-32">
      {/* Hero Section */}
      <section className={`relative min-h-[90vh] flex items-center ${isEmbed ? 'pt-4' : 'pt-20'}`}>
        <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8 z-20"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-glass-border bg-glass-surface backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-glass-cyan animate-pulse"></span>
              <span className="text-xs font-medium text-gray-300 tracking-wide uppercase">{t('home.badge')}</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-display font-bold leading-tight">
              {t('home.title1')} <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-glass-cyan to-white text-glow">
                {t('home.title2')}
              </span> <br />
              {t('home.title3')}
            </h1>

            <p className="text-xl text-gray-400 max-w-lg leading-relaxed">
              {t('home.subtitle')}
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <button
                onClick={handleGetStarted}
                className="glass-button-primary flex items-center gap-2 group"
              >
                {t('home.ctaGetStarted')}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => navigate('/docs')}
                className="glass-button text-gray-300 hover:text-white"
              >
                {t('home.ctaDocs')}
              </button>
            </div>
          </motion.div>

          <div className="relative z-10 lg:h-full flex items-center justify-center">
            <PrismHero />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 space-y-4"
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold">{t('home.whyTitle')}</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {t('home.whySubtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <GlassCard key={index} className="h-full group">
              <div className="mb-6 p-3 bg-glass-surface w-fit rounded-lg border border-glass-border group-hover:border-glass-cyan/50 transition-colors">
                {feature.icon}
              </div>
              <h3 className="text-xl font-display font-bold mb-3 text-white group-hover:text-glass-cyan transition-colors">
                {feature.title}
              </h3>
              <p className="text-gray-400 leading-relaxed">
                {feature.description}
              </p>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* Audience Section */}
      <section className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <GlassCard className="p-12 border-l-4 border-l-glass-cyan">
            <div className="flex items-center gap-4 mb-6">
              <Code2 className="w-8 h-8 text-glass-cyan" />
              <h3 className="text-2xl font-display font-bold">{t('home.forDevelopers')}</h3>
            </div>
            <ul className="space-y-4 text-gray-400">
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-glass-cyan rounded-full" />
                {t('home.devFeature1')}
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-glass-cyan rounded-full" />
                {t('home.devFeature2')}
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-glass-cyan rounded-full" />
                {t('home.devFeature3')}
              </li>
            </ul>
          </GlassCard>

          <GlassCard className="p-12 border-l-4 border-l-white">
            <div className="flex items-center gap-4 mb-6">
              <Users className="w-8 h-8 text-white" />
              <h3 className="text-2xl font-display font-bold">{t('home.forTeams')}</h3>
            </div>
            <ul className="space-y-4 text-gray-400">
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                {t('home.teamFeature1')}
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                {t('home.teamFeature2')}
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                {t('home.teamFeature3')}
              </li>
            </ul>
          </GlassCard>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6 text-center">
        <div className="glass-panel p-16 max-w-4xl mx-auto relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-glass-cyan/5 to-transparent opacity-50" />

          <div className="relative z-10 space-y-8">
            <h2 className="text-4xl md:text-5xl font-display font-bold">
              {t('home.readyToShip')}
            </h2>
            <p className="text-xl text-gray-400">
              {t('home.joinThousands')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleTopUp}
                className="glass-button-primary w-full sm:w-auto"
              >
                {t('home.ctaTopUp')}
              </button>
              <button className="glass-button w-full sm:w-auto">
                {t('home.ctaContact')}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
